import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { DateRangePicker, SelectPicker } from "rsuite";
import * as dashboardService from "../services/dashboard.services";
import { useTranslation } from "react-i18next";

const TIME_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "Last 7 Days", value: "this_week" },
  { label: "Last 30 Days", value: "last_month" },
  { label: "Last 5 Months", value: "last_5_months" },
  { label: "Custom Range", value: "custom_range" },
];

const DEFAULT_TIME_LIMIT = "last_5_months";

const getChartTitle = (name, timeLimit) => {
  return timeLimit === "last_5_months" ? `MONTHLY ${name}` : name;
};

const Dashboard = () => {
  const { t } = useTranslation();
  const [showDashboardCount, setShowDashboardCount] = useState({});

  const [timeLimit, setTimeLimit] = useState(DEFAULT_TIME_LIMIT);
  const [customRange, setCustomRange] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const [revenueData, setRevenueData] = useState({ labels: [], amounts: [] });
  const [revenueLoading, setRevenueLoading] = useState(true);

  const [ioData, setIoData] = useState({ labels: [], items: [], outfits: [] });
  const [ioLoading, setIoLoading] = useState(true);

  useEffect(() => {
    dashboardService
      .Counts()
      .then((response) => {
        setShowDashboardCount(response && response.data ? response.data : {});
      })
      .catch((error) => console.log("error ====> ", error));
  }, []);

  useEffect(() => {
    if (timeLimit !== "custom_range" || customRange) {
      setRevenueLoading(true);
      setIoLoading(true);

      const customRangeParam = JSON.stringify(customRange);

      Promise.all([
        dashboardService.RevenueOverview(timeLimit, customRangeParam),
        dashboardService.ItemsOutfitsOverview(timeLimit, customRangeParam),
      ])
        .then(([revenueRes, ioRes]) => {
          setRevenueData(revenueRes?.data || { labels: [], amounts: [] });
          setIoData(ioRes?.data || { labels: [], items: [], outfits: [] });
        })
        .catch(() => {})
        .finally(() => {
          setRevenueLoading(false);
          setIoLoading(false);
        });
    }
  }, [timeLimit, customRange]);

  const handleTimeSelect = (value) => {
    setTimeLimit(value);
    if (value === "custom_range") {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
      setCustomRange(null);
    }
  };

  const handleReset = () => {
    setTimeLimit(DEFAULT_TIME_LIMIT);
    setCustomRange(null);
    setShowDatePicker(false);
  };

  const barChartBase = {
    chart: { toolbar: { show: false }, zoom: { enabled: false } },
    plotOptions: {
      bar: { horizontal: false, columnWidth: "45%", borderRadius: 4 },
    },
    dataLabels: { enabled: false },
    grid: { borderColor: "#f0f0f0" },
    legend: { position: "top" },
  };

  const revenueChartOptions = {
    ...barChartBase,
    chart: { ...barChartBase.chart, type: "bar" },
    colors: ["#6f42c1"],
    xaxis: {
      categories: revenueData.labels,
      labels: { style: { fontSize: "12px" } },
    },
    yaxis: {
      labels: {
        formatter: (val) => `₹${Math.round(val)}`,
        style: { fontSize: "12px" },
      },
    },
    tooltip: { y: { formatter: (val) => `₹${val}` } },
  };

  const revenueChartSeries = [{ name: "Revenue", data: revenueData.amounts }];

  const ioChartOptions = {
    ...barChartBase,
    chart: { ...barChartBase.chart, type: "bar" },
    colors: ["#6f42c1", "#f1388b"],
    xaxis: {
      categories: ioData.labels,
      labels: { style: { fontSize: "12px" } },
    },
    yaxis: {
      labels: {
        formatter: (val) => Math.round(val),
        style: { fontSize: "12px" },
      },
    },
    tooltip: { y: { formatter: (val) => Math.round(val) } },
  };

  const ioChartSeries = [
    { name: "Items", data: ioData.items },
    { name: "Outfits", data: ioData.outfits },
  ];

  const chartsLoading = revenueLoading || ioLoading;

  return (
    <>
      <div className="row row-sm animation_fade">
        {/* ── Count Cards ── */}
        <div className="col-sm-12 col-lg-12 col-xl-12">
          <div className="row row-sm">
            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <div className="card custom-card">
                <div className="card-body cp">
                  <Link to="/admin/customerManagement/customers/list/1">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-people-fill" viewBox="0 0 16 16">
                          <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7Zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5.784 6A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216ZM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">{t("sidebar_link_users")}</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.users || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>

            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <div className="card custom-card">
                <div className="card-body cp">
                  <Link to="/admin/outfitManagement/list/1">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-people-fill" viewBox="0 0 16 16">
                          <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7Zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5.784 6A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216ZM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">{t("sidebar_link_outfit")}</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.outfits || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>

            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <div className="card custom-card">
                <div className="card-body cp">
                  <Link to="/admin/itemManagement/list/1">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-people-fill" viewBox="0 0 16 16">
                          <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7Zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5.784 6A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216ZM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">{t("sidebar_link_item")}</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.items || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>

            <div className="col-sm-12 col-md-6 col-lg-6 col-xl-3">
              <div className="card custom-card">
                <div className="card-body cp">
                  <Link to="/admin/posts/list/1">
                    <div className="card-item">
                      <div className="card-item-icon card-icon cp">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" className="bi bi-people-fill" viewBox="0 0 16 16">
                          <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7Zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5.784 6A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216ZM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                        </svg>
                      </div>
                      <div className="card-item-title mb-2">
                        <label className="main-content-label tx-13 font-weight-bold mb-1">{t("sidebar_link_post")}</label>
                      </div>
                      <div className="card-item-body">
                        <div className="card-item-stat">
                          <h4 className="font-weight-bold">{showDashboardCount?.posts || "0"}</h4>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Shared Filter ── */}
        <div className="col-sm-12 col-lg-12 col-xl-12 mt-2">
          <div className="d-flex justify-content-end align-items-center flex-wrap gap-2">
            <SelectPicker
              value={timeLimit}
              cleanable={false}
              onChange={handleTimeSelect}
              size="sm"
              data={TIME_OPTIONS}
              labelKey="label"
              valueKey="value"
              className="dashboard-filter-select"
              searchable={false}
              placeholder="Select Date"
              style={{ width: 160 }}
              renderValue={(value) => {
                const option = TIME_OPTIONS.find((opt) => opt.value === value);
                return option?.label || "Select Date";
              }}
            />
            {showDatePicker && (
              <div className="rangepicker_container filter_design">
                <DateRangePicker
                  size="sm"
                  placeholder="Select Date"
                  editable={false}
                  placement="bottomEnd"
                  disabledDate={(date) => {
                    const year = date.getFullYear();
                    return year < 2000 || year > 2099;
                  }}
                  onChange={(range) => {
                    setCustomRange(
                      range && range.length === 2
                        ? { start: range[0], end: range[1] }
                        : null
                    );
                  }}
                />
              </div>
            )}
            <button
              type="button"
              className="btn btn-sm dashboard-reset-btn"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
        </div>

        {/* ── Charts ── */}
        <div className="col-sm-12 col-lg-12 col-xl-12 mt-2">
          <div className="row row-sm">

            {/* Revenue Overview */}
            <div className="col-xl-6 col-lg-12 col-md-12 col-sm-12">
              <div className="card custom-card">
                <div className="card-body">
                  <h6 className="main-content-label mb-3">
                    {getChartTitle("REVENUE", timeLimit)}
                  </h6>
                  {chartsLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                    </div>
                  ) : revenueData.labels.length === 0 ? (
                    <div className="text-center py-5 text-muted">No revenue data for this period</div>
                  ) : (
                    <ReactApexChart
                      options={revenueChartOptions}
                      series={revenueChartSeries}
                      type="bar"
                      height={300}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Items & Outfits Overview */}
            <div className="col-xl-6 col-lg-12 col-md-12 col-sm-12">
              <div className="card custom-card">
                <div className="card-body">
                  <h6 className="main-content-label mb-3">
                    Items and Outfits Overview
                  </h6>
                  {chartsLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                    </div>
                  ) : ioData.labels.length === 0 ? (
                    <div className="text-center py-5 text-muted">No data for this period</div>
                  ) : (
                    <ReactApexChart
                      options={ioChartOptions}
                      series={ioChartSeries}
                      type="bar"
                      height={300}
                    />
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
